import { collection, getDocs } from "firebase/firestore";
import { DATABASE } from "../Config/firebase";
import { applyKycProfileFill, fetchAllClientsForMatching, fetchClientsByIds, planKycProfileFill } from "./kycService";
import {
  aggregateFillPreview,
  buildImportOverrides,
  canCreateProfileForStatus,
  matchImportEntry,
  orphanIdForEntry,
  validateImportEntries,
} from "../Utils/kycBulkFill";

const KYC_COLLECTION = "kyc_profiles";

async function existingProfileIds() {
  const snap = await getDocs(collection(DATABASE, KYC_COLLECTION));
  return new Set(snap.docs.map((d) => d.id));
}

async function decideAllowCreate(user, existing) {
  if (existing.has(user.user_id)) return { allowCreate: true, skipReason: null };
  if (canCreateProfileForStatus(user.status)) return { allowCreate: true, skipReason: null };
  return { allowCreate: false, skipReason: "status_not_allowed" };
}

async function planOneUser(user, existing, source) {
  const { allowCreate, skipReason } = await decideAllowCreate(user, existing);
  if (skipReason) {
    return { uid: user.user_id, created: false, filledCount: 0, fields: [], skippedReason: skipReason };
  }
  return planKycProfileFill(user.user_id, {
    allowCreate,
    source,
    matchMethod: "firebase_user",
  });
}

export async function previewSyncAllFirebaseClients() {
  const users = await fetchAllClientsForMatching();
  const existing = await existingProfileIds();
  const rows = [];
  for (const user of users) {
    try {
      rows.push(await planOneUser(user, existing, "admin_sync_all"));
    } catch (_e) {
      rows.push({ uid: user.user_id, error: true, filledCount: 0, fields: [] });
    }
  }
  return aggregateFillPreview(rows);
}

export async function runSyncAllFirebaseClients() {
  const users = await fetchAllClientsForMatching();
  const existing = await existingProfileIds();
  const rows = [];
  for (const user of users) {
    try {
      const { allowCreate, skipReason } = await decideAllowCreate(user, existing);
      if (skipReason) {
        rows.push({ uid: user.user_id, created: false, filledCount: 0, fields: [], skippedReason: skipReason });
        continue;
      }
      rows.push(
        await applyKycProfileFill(user.user_id, {
          allowCreate,
          source: "admin_sync_all",
          matchMethod: "firebase_user",
        })
      );
    } catch (_e) {
      rows.push({ uid: user.user_id, error: true, filledCount: 0, fields: [] });
    }
  }
  return aggregateFillPreview(rows);
}

async function planImportRow(entry, users, existing) {
  const { user, method, confidence } = matchImportEntry(entry, users);
  if (method === "ambiguous") {
    return { filledCount: 0, fields: [], skippedReason: "ambiguous_match" };
  }
  if (!user) {
    return {
      ...(await planKycProfileFill(orphanIdForEntry(entry), {
        isOrphan: true,
        allowCreate: true,
        overrides: buildImportOverrides(entry, confidence),
        source: "admin_import_scan",
        matchMethod: "unmatched",
      })),
      isOrphan: true,
    };
  }
  const { allowCreate, skipReason } = await decideAllowCreate(user, existing);
  if (skipReason) {
    return { uid: user.user_id, created: false, filledCount: 0, fields: [], skippedReason: skipReason };
  }
  return planKycProfileFill(user.user_id, {
    allowCreate,
    overrides: buildImportOverrides(entry, confidence),
    source: "admin_import_scan",
    matchMethod: method,
  });
}

export async function previewImportScannedDataset(dataset) {
  const { accepted, rejected } = validateImportEntries(dataset?.clients || []);
  const users = await fetchAllClientsForMatching();
  const existing = await existingProfileIds();
  const rows = [];
  for (const { entry } of accepted) {
    try {
      rows.push(await planImportRow(entry, users, existing));
    } catch (_e) {
      rows.push({ error: true, filledCount: 0, fields: [] });
    }
  }
  return aggregateFillPreview(rows, rejected);
}

export async function runImportScannedDataset(dataset) {
  const { accepted, rejected } = validateImportEntries(dataset?.clients || []);
  const users = await fetchAllClientsForMatching();
  const existing = await existingProfileIds();
  const rows = [];
  for (const { entry } of accepted) {
    try {
      const { user, method, confidence } = matchImportEntry(entry, users);
      if (method === "ambiguous") {
        rows.push({ filledCount: 0, fields: [], skippedReason: "ambiguous_match" });
        continue;
      }
      if (!user) {
        rows.push({
          ...(await applyKycProfileFill(orphanIdForEntry(entry), {
            isOrphan: true,
            allowCreate: true,
            overrides: buildImportOverrides(entry, confidence),
            source: "admin_import_scan",
            matchMethod: "unmatched",
          })),
          isOrphan: true,
        });
        continue;
      }
      const { allowCreate, skipReason } = await decideAllowCreate(user, existing);
      if (skipReason) {
        rows.push({ uid: user.user_id, created: false, filledCount: 0, fields: [], skippedReason: skipReason });
        continue;
      }
      rows.push(
        await applyKycProfileFill(user.user_id, {
          allowCreate,
          overrides: buildImportOverrides(entry, confidence),
          source: "admin_import_scan",
          matchMethod: method,
        })
      );
    } catch (_e) {
      rows.push({ error: true, filledCount: 0, fields: [] });
    }
  }
  return aggregateFillPreview(rows, rejected);
}

export async function previewRefreshRegistrationPhotos() {
  const snap = await getDocs(collection(DATABASE, KYC_COLLECTION));
  const ids = snap.docs.filter((d) => !d.data()?.is_orphan).map((d) => d.id);
  const users = (await fetchClientsByIds(ids)).filter((u) => u.document);
  const rows = [];
  for (const user of users) {
    try {
      rows.push(
        await planKycProfileFill(user.user_id, {
          allowCreate: false,
          source: "admin_sync_registration_photos",
        })
      );
    } catch (_e) {
      rows.push({ uid: user.user_id, error: true, filledCount: 0, fields: [] });
    }
  }
  return aggregateFillPreview(rows);
}

export async function runRefreshRegistrationPhotos() {
  const snap = await getDocs(collection(DATABASE, KYC_COLLECTION));
  const ids = snap.docs.filter((d) => !d.data()?.is_orphan).map((d) => d.id);
  const users = (await fetchClientsByIds(ids)).filter((u) => u.document);
  const rows = [];
  for (const user of users) {
    try {
      rows.push(
        await applyKycProfileFill(user.user_id, {
          allowCreate: false,
          source: "admin_sync_registration_photos",
        })
      );
    } catch (_e) {
      rows.push({ uid: user.user_id, error: true, filledCount: 0, fields: [] });
    }
  }
  return aggregateFillPreview(rows);
}
